/**
 * The campaign funnel's own modal.
 *
 * Deliberately NOT `<AppModal>`, by decision on 6 Oct 2026. The repo rule
 * says main-frontend modals use the shared shell, and `SocialPosts` is
 * already exempt because it lives on its own canvas. These pages have the
 * same claim, twice over: they render in one of two self-contained skins
 * (`cmp` and `mk`, Or's reproduction), neither of which shares the
 * authenticated app's navy-header chrome, and `/c/*` is seen by cold ad
 * clicks who have never met the product. Dropping the app's dialog on top
 * of Or's design would break the one thing that design is for.
 *
 * It is a modal, so it owes the usual duties rather than just a box:
 * Escape, a backdrop click, a labelled dialog role, focus moved in on open
 * and handed back on close, and the page behind it held still.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import './CampaignModal.css';

export default function CampaignModal({
  isOpen, title, onClose, skin = 'cmp', children, footer, maxWidth = 560, testId,
}) {
  const closeRef = useRef(null);
  const returnTo = useRef(null);

  const close = useCallback(() => { if (onClose) onClose(); }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    // Hand focus back to whatever opened this. Without it, closing drops
    // the caret at the top of the document and a keyboard user has to
    // tab the whole page again to get back to where they were.
    returnTo.current = document.activeElement;
    closeRef.current?.focus();

    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } };
    document.addEventListener('keydown', onKey);

    // These pages scroll inside `.cmp-page` / `.mk`, not on <body> — the
    // app's global CSS pins html and body. Locking <body> would do nothing.
    const scroller = document.querySelector('.mk, .cmp-page');
    const previous = scroller?.style.overflow;
    if (scroller) scroller.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      if (scroller) scroller.style.overflow = previous || '';
      const back = returnTo.current;
      if (back && typeof back.focus === 'function') back.focus();
    };
  }, [isOpen, close]);

  if (!isOpen) return null;

  const mk = skin === 'mk';
  const labelId = `campaign-modal-title-${skin}`;

  return (
    <div
      className={`campaign-modal${mk ? ' campaign-modal--mk' : ''}`}
      data-testid={testId}
      // The backdrop closes, but only the backdrop: a click that started
      // inside the card and drifted out while selecting text must not.
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        className="campaign-modal-card"
        style={{ maxWidth }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? labelId : undefined}
      >
        <div className="campaign-modal-head">
          {title && <h2 className="campaign-modal-title" id={labelId}>{title}</h2>}
          <button
            type="button"
            ref={closeRef}
            className="campaign-modal-x"
            onClick={close}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="campaign-modal-body">{children}</div>

        {footer !== undefined ? footer : (
          <div className="campaign-modal-foot">
            <button type="button" className="campaign-modal-close" onClick={close}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
